import { Box, Stack } from '@mui/material'
import { useState, type ReactNode } from 'react'
import { ListBulletsIcon, RowsIcon } from '@phosphor-icons/react'
import { CardItem, tokens, ViewSwitch } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import type { TenantImportRow } from '@einwert/valuation-api-client'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { TenantDetailsTable } from 'ComponentLibrary/src/widgets/TenantTables/TenantDetailsTable/TenantDetailsTable'
import type { TenantDetailsTableRow } from 'ComponentLibrary/src/widgets/TenantTables/TenantDetailsTable/types'
import { rowFieldErrors } from './review'
import { tenantImportPreviewColumns } from './previewColumns'
import { toPreviewTenant, type PreviewTenant } from './preview'
import { tenantImportCopy, type TenantImportLanguage } from './copy'
import type { TenantDetailsTableCopy } from 'ComponentLibrary/src/widgets/TenantTables/TenantDetailsTable/TenantDetailsTable'

export function TenantImportPreview({
    rows,
    structures,
    renderSummary,
    initialView = 'category',
    language = 'de',
    tableCopy,
    maxHeight,
    pageLayout,
}: {
    rows: TenantImportRow[]
    initialView?: 'category' | 'detail'
    structures: { uuid: string; title: string }[]
    language?: TenantImportLanguage
    tableCopy?: TenantDetailsTableCopy
    maxHeight?: string
    pageLayout?: { rowsLabel: (count: number) => string; summaryRowCount: number }
    renderSummary?: (tenants: PreviewTenant[]) => ReactNode
}) {
    const copy = tenantImportCopy(language)
    const tableViews = [
        { label: pageLayout ? copy.summary : copy.categories, value: 'category', leftIcon: <RowsIcon /> },
        { label: pageLayout ? copy.details : 'Details', value: 'detail', leftIcon: <ListBulletsIcon /> },
    ]
    const [view, setView] = useState<string>(initialView)
    const showCategories = !!renderSummary && rows.length > 1 && view === 'category'
    const structureIds = structures.map((structure) => structure.uuid)
    const fieldErrors: Record<string, string | undefined>[] = rows.map((row) => {
        const errors = rowFieldErrors(row)
        return { ...errors, valuationStructureIndex: errors.structure_uuid, area: errors.area_sqm }
    })
    const tenants = rows.map(toPreviewTenant)
    const tenantRows: TenantDetailsTableRow[] = tenants.map((tenant, tenantIndex) => ({
        ...tenant,
        tenantIndex,
        valuationStructureIndex: rows[tenantIndex].structure_uuid ? structureIds.indexOf(rows[tenantIndex].structure_uuid) : undefined,
    }))
    const Container = pageLayout ? Stack : CardItem
    return (
        <Container sx={{ display: 'flex', flexDirection: 'column', gap: pageLayout ? 0 : tokens.SizingSpacing200, minWidth: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={tokens.SizingSpacing100}
                flexWrap="wrap"
                sx={
                    pageLayout
                        ? {
                              padding: tokens.SizingSpacing300,
                              backgroundColor: tokens.ColorSurfaceDefault,
                              boxShadow: '0px 5px 15px -6px #e6e6e6ff',
                              position: 'relative',
                              zIndex: 5,
                          }
                        : undefined
                }
            >
                {pageLayout ? (
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {pageLayout.rowsLabel(showCategories ? pageLayout.summaryRowCount : rows.length)}
                    </Typography>
                ) : (
                    <Headline title={showCategories ? copy.summary : copy.details} variant="headline3" fullWidth={false} />
                )}
                {renderSummary && rows.length > 1 && <ViewSwitch items={tableViews} value={view} onClick={setView} />}
            </Stack>
            <Box
                sx={{
                    minWidth: 0,
                    maxWidth: '100%',
                    maxHeight: showCategories ? (maxHeight ?? 520) : undefined,
                    overflow: showCategories ? 'auto' : undefined,
                    ...(pageLayout && {
                        paddingX: tokens.SizingSpacing200,
                        paddingBottom: tokens.SizingSpacing200,
                        backgroundColor: tokens.ColorSurfaceDefault,
                    }),
                }}
            >
                {showCategories ? (
                    renderSummary?.(tenants)
                ) : (
                    <TenantDetailsTable
                        tenantRows={tenantRows}
                        copy={tableCopy}
                        locale={language === 'en' ? 'en-GB' : 'de-DE'}
                        maxHeight={maxHeight ?? 'min(520px, 50vh)'}
                        structureOptions={structures.map((structure, value) => ({ value, label: structure.title }))}
                        getColumns={(columns) => [
                            ...columns.filter((column) =>
                                [
                                    'reference_number',
                                    ...(structures.length > 0 ? ['valuationStructureIndex'] : []),
                                    'usage_type',
                                    'area',
                                    'rental_status',
                                    'tenant_name',
                                    'floor',
                                    'sub_usage_type',
                                    'number_of_units',
                                    'lease_number',
                                    'start_date',
                                    'end_date',
                                    'options',
                                    'note',
                                ].includes(column.id ?? ('accessorKey' in column ? String(column.accessorKey) : ''))
                            ),
                            ...tenantImportPreviewColumns(tenants, fieldErrors, language),
                        ]}
                        columnPinning={{ left: ['reference_number'] }}
                        getCellError={(row, columnId) => fieldErrors[row.tenantIndex][columnId]}
                    />
                )}
            </Box>
        </Container>
    )
}
